'use client'

import { Skeleton } from '@/components/ui/skeleton'
import { useQuery } from '@tanstack/react-query'
import { format, parseISO } from 'date-fns'
import { motion } from 'framer-motion'
import { ArrowRightIcon } from 'lucide-react'
import Link from 'next/link'
import * as React from 'react'

import { GridSection } from '@/components/marketing/fragments/grid-section'
import { SiteHeading } from '@/components/marketing/fragments/site-heading'
import { AnimatedGridPattern } from '@/components/ui/animated-grid-pattern'
import { FillRemainingSpace } from '@/components/ui/fill-remaining-space'
import { Routes } from '@/constants/routes'
import { BlogService } from '@/lib/api'
import { getBaseUrl } from '@/lib/urls/get-base-url'
import { BlogDataType } from '@/types/response-api'
import { msg } from '@lingui/core/macro'
import { useLingui } from '@lingui/react'

export function BlogPosts(): React.JSX.Element {
  const { i18n } = useLingui()
  const currentLanguage = i18n.locale
  const {
    data: allPosts = {},
    error,
    isLoading
  } = useQuery({
    queryKey: ['news', currentLanguage],
    queryFn: async () => {
      const language = ['en', 'vi'].includes(currentLanguage) ? currentLanguage : 'en'
      return await BlogService.fetchNews(language)
    }
  })

  if (isLoading) {
    return (
      <div className='relative'>
        <AnimatedGridPattern />
        <GridSection>
          <div className='bg-diagonal-lines'>
            <div className='flex flex-col bg-background md:px-12 px-4 py-20 md:py-24 lg:mx-12 lg:border-x overflow-hidden'>
              <SiteHeading
                badge={msg`Blog Posts`}
                title={msg`Insights & News`}
                description={msg`Learn more from members of our team and industry-leading experts.`}
              />
              <div className='grid gap-x-8 gap-y-8 divide-y md:grid-cols-2 md:gap-x-6 md:divide-none xl:grid-cols-3'>
                {[...Array(6)].map((_, index) => (
                  <motion.div
                    key={index}
                    initial={{ opacity: 0, y: 50 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    viewport={{ once: true }}
                  >
                    <div className='flex h-full flex-col overflow-hidden bg-gradient-to-br from-white/90 to-gray-50/90 dark:from-neutral-900/90 dark:to-neutral-800/90 rounded-2xl shadow-md border border-gray-200/50 dark:border-neutral-800/50'>
                      <Skeleton className='w-full h-52' />
                      <div className='flex flex-col p-6 space-y-4'>
                        <div className='flex flex-row items-center justify-between'>
                          <Skeleton className='h-4 w-24' />
                          <Skeleton className='h-4 w-20' />
                        </div>
                        <Skeleton className='h-4 w-full' />
                        <Skeleton className='h-4 w-5/6' />
                        <Skeleton className='h-4 w-2/3' />
                        <div className='flex flex-row items-center justify-between mt-4'>
                          <Skeleton className='h-4 w-20' />
                        </div>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </div>
        </GridSection>
      </div>
    )
  }

  if (error) {
    return <div className='py-20 text-center text-red-500'>{i18n._(`Error loading news`)}</div>
  }

  return (
    <div className='relative'>
      <AnimatedGridPattern />
      <GridSection>
        <div className='bg-diagonal-lines'>
          <div className='flex flex-col bg-background md:px-12 px-4 py-20 md:py-24 lg:mx-12 lg:border-x overflow-hidden'>
            <SiteHeading
              badge={msg`Blog Posts`}
              title={msg`Insights & News`}
              description={msg`Learn more from members of our team and industry-leading experts.`}
            />
            <div className='grid gap-x-8 gap-y-8 divide-y md:grid-cols-2 md:gap-x-6 md:divide-none xl:grid-cols-3'>
              {(allPosts as BlogDataType)?.rows?.slice().map((post, index: number) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link
                    href={`${getBaseUrl()}${Routes.Blog}/${post.slug}`}
                    className='flex h-full flex-col overflow-hidden bg-gradient-to-br from-white/90 to-gray-50/90 dark:from-neutral-900/90 dark:to-neutral-800/90 rounded-2xl shadow-md border border-gray-200/50 dark:border-neutral-800/50 group'
                  >
                    <div
                      className='w-full h-52 bg-cover bg-center transition-transform duration-300 group-hover:scale-105'
                      style={{ backgroundImage: `url(${post.image})` }}
                    />
                    <div className='flex flex-col p-5 space-y-4 relative'>
                      <span className='absolute inset-0 z-0'>
                        <span className='absolute -top-2 -left-2 w-16 h-16 bg-blue-400/10 blur-xl transition-opacity duration-300 opacity-0 group-hover:opacity-100' />
                        <span className='absolute -bottom-2 -right-2 w-16 h-16 bg-orange-400/10 blur-xl transition-opacity duration-300 opacity-0 group-hover:opacity-100' />
                      </span>
                      <div className='relative z-10'>
                        <div className='flex flex-row items-center justify-between text-muted-foreground'>
                          <span className='capitalize text-sm font-medium bg-gradient-to-r from-orange-500 to-blue-500 bg-clip-text text-transparent'>
                            {post.name_type || i18n._('Uncategorized')}
                          </span>
                          <time className='text-sm font-medium' dateTime={post.created_at}>
                            {format(parseISO(post.created_at), 'dd MMM yyyy')}
                          </time>
                        </div>
                        <h2 className='text-xl font-bold text-foreground line-clamp-2 mt-2 capitalize'>
                          {post.title}
                        </h2>
                        <p className='line-clamp-3 capitalize text-muted-foreground text-sm leading-relaxed mt-2'>
                          {post.description}
                        </p>
                        <FillRemainingSpace />
                        <div className='flex flex-row items-center justify-between mt-4'>
                          <div className='flex items-center gap-2 text-sm font-medium text-orange-600 hover:text-orange-700 transition-colors duration-300'>
                            {i18n._(`Read more`)}
                            <ArrowRightIcon className='size-4 shrink-0 transition-transform group-hover:translate-x-1' />
                          </div>
                        </div>
                      </div>
                    </div>
                  </Link>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </GridSection>
    </div>
  )
}
